import React from "react"
import PropTypes from "prop-types"

import {
    createProfileToggleSingleFilter,
    // sorting functions
    enumLabelSort,
    enumValueSort,
    enumPrecedenceSort,
    textGenericFilter,
    enumOrderSort,
    createSchoolDistrictEnrollmentFilter,
} from "shared/search/containers/Filter/Filters"
import {
    enumGenericFilter,
    searchifySelectGenericFilter,
    enumOrderDescendingSort,
} from "shared/search/containers/Filter/FilterCreators"

import {
    ArticleFilterableListView,
    BulkUploadFileFLV,
    IssueManagementFilterableListView,
    PressContactSearchifyFLV,
    PublicOrganizationFilterableListView,
    TransactionFilterableListView,
    SocialDocumentFilterableListView,
    DocumentInOrganizationProfileFilterableListView,
} from "shared/search/containers/FilterableListView/FilterableListViews"

import {
    ArticleSelect,
    BulkUploadFileSelect,
    DMASelect,
    IssueManagementSelect,
    PressContactSelect,
    PublicOrganizationSelect,
    TransactionSelect,
    SocialMediaSelect,
    DocumentSelect,
    SchoolDistrictSelect,
} from "app/static/frontend/selects/containers/PieSelect"

import { CirculationSelect, FrequencySelect, LanguageSelect } from "app/static/frontend/selects/containers/EnumSelect"

import { internationalRegions, euRegion, usAndCanadaRegions, localRegions } from "shared/imports/regionConstants"

import isFeatureEnabled from "shared/featureflags/helperFunctions"
import { checkIfShouldShowSchoolBoard } from "shared/imports/permissionFunctions"

export class EUPublicOrganizationCustomTypeFilter extends React.Component {
    static defaultProps = {
        filterComponent: enumGenericFilter,
        uniqueFilterId: "euCustomPublicOrgTypeFilter",
        filterKey: "organization_type__in",
        enumItems: DjangIO.app.person.types.PublicOrganizationType.region_supported_items().filter(
            (item) => item.value === DjangIO.app.person.types.PublicOrganizationType.custom.value,
        ),
        regions: euRegion,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationTypeFilter extends React.Component {
    static defaultProps = {
        filterComponent: enumGenericFilter,
        uniqueFilterId: "publicOrgTypeFilter",
        filterKey: "organization_type__in",
        enumItems: DjangIO.app.person.types.PublicOrganizationType.region_supported_items().filter((item) => {
            // For School Boards, we need to check if the user doesn't have other local permissions.
            // This is due the overlapping between both permissions and what we actually need to show in the filter.
            const { isExclusivelySchoolBoard, canSeeSchoolBoards } = checkIfShouldShowSchoolBoard()
            const isSchoolDistrictOffice =
                item.value === DjangIO.app.person.types.PublicOrganizationType.school_district_office.value
            if (isExclusivelySchoolBoard) {
                return isSchoolDistrictOffice
            }
            if (!canSeeSchoolBoards && isSchoolDistrictOffice) {
                return false
            }

            const excludedValues = [
                DjangIO.app.person.types.PublicOrganizationType.secretariat_general.value,
                DjangIO.app.person.types.PublicOrganizationType.directorate_general.value,
            ]

            if (excludedValues.includes(item.value)) {
                return false
            }
            return true
        }),
        enumGroups: DjangIO.app.person.types.PublicOrganizationType.filter_groups.map((item) => ({
            ...item,
            collapseGroup: item.title && item.title.toLowerCase() === "custom",
        })),
        excludedRegions: euRegion,
        sortBy: enumValueSort,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class EUNewMajorOrganizationTypeFilter extends React.Component {
    static defaultProps = {
        filterComponent: enumGenericFilter,
        uniqueFilterId: "publicOrganizationTypeFilter",
        filterKey: "eu_new_major_organization_type",
        enumItems: DjangIO.app.person.newtypes.NewMajorOrganizationType.region_supported_items(),
        enumGroups: DjangIO.app.person.newtypes.NewMajorOrganizationType.filter_groups,
        onlyRegions: euRegion,
        sortBy: enumPrecedenceSort,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationTypeInternationalFilter extends React.Component {
    static defaultProps = {
        filterComponent: enumGenericFilter,
        uniqueFilterId: "publicOrganizationTypeInternationalFilter",
        filterKey: "contains_major_organization_type",
        enumItems: DjangIO.app.person.newtypes.NewMajorOrganizationType.region_supported_items(),
        enumGroups: DjangIO.app.person.newtypes.NewMajorOrganizationType.filter_groups.filter(
            (fg) => fg.title === "Organization Types",
        ),
        regions: internationalRegions,
        sortBy: enumOrderSort,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationParentSearchifyFilter extends React.Component {
    static get defaultProps() {
        return {
            filterComponent: searchifySelectGenericFilter,
            uniqueFilterId: "parentOrg",
            filterKey: "parents",
            searchifyFLV: PublicOrganizationFilterableListView,
            dataTypeLabel: "Parent Organization",
            selectComponent: PublicOrganizationSelect,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationFromBulkUploadFilter extends React.Component {
    static get defaultProps() {
        return {
            filterComponent: searchifySelectGenericFilter,
            uniqueFilterId: "orgFromBulkUploadFilter",
            filterKey: "from_bulk_upload_files",
            searchifyFLV: BulkUploadFileFLV,
            searchifyPermanentFilter: {
                upload_type: DjangIO.app.custom_data.types.BulkUploadType.public_organization.value,
                status: DjangIO.app.custom_data.types.BulkUploadStatus.finished.value,
            },
            dataTypeLabel: "Custom Organization Bulk Uploads",
            displayLabel: "Bulk upload files...",
            selectComponent: BulkUploadFileSelect,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export const PublicOrganizationOrganizatonsFilter = createProfileToggleSingleFilter({
    uniqueFilterId: "publicOrganizationOrganizatonsFilter",
    trueText: "Current Org Only",
    filterKeyMapping: {
        true: "parent_organization",
    },
})

export const PublicOrganizationProjectFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={IssueManagementFilterableListView} {...props} />
}
PublicOrganizationProjectFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    searchifyFLV: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
PublicOrganizationProjectFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "publicOrganizationProjectFilter",
    filterKey: "for_project_and_subprojects",
    dataTypeLabel: "Issues",
    selectComponent: IssueManagementSelect,
}

export const PublicOrganizationTransactionFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={TransactionFilterableListView} {...props} />
}
PublicOrganizationTransactionFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    searchifyFLV: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
PublicOrganizationTransactionFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "publicOrganizationTransactionFilter",
    filterKey: "organization_transaction",
    dataTypeLabel: "Transaction",
    displayLabel: "Transaction",
    selectComponent: TransactionSelect,
}

export class PublicOrganizationPacOrgTypeFilter extends React.Component {
    static defaultProps = {
        filterComponent: enumGenericFilter,
        uniqueFilterId: "publicOrgPacOrgTypeFilter",
        filterKey: "pac_org_type__in",
        enumItems: DjangIO.app.pac.types.EntityType.items().filter((item) => item.user_selectable_pac_org_type),
        sortBy: enumValueSort,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

// Outlet Specific Filters
export class PublicOrganizationOutletMetaTypeFilter extends React.Component {
    static get defaultProps() {
        return {
            filterComponent: enumGenericFilter,
            uniqueFilterId: "publicOrgOutletMetaTypeFilter",
            filterKey: "outlet_type",
            enumItems: DjangIO.app.person.newtypes.MetaOrganizationType.items().filter(
                (type) => type.parent === DjangIO.app.person.newtypes.SectorType.media.value,
            ),
            enumGroups: DjangIO.app.person.newtypes.MetaOrganizationType.filter_groups,
            sortBy: enumValueSort,
            permissions: [DjangIO.app.models.PermissionType.media_relations],
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationOutletCategoryFilter extends React.Component {
    static get defaultProps() {
        return {
            displayLabel: "Outlet Category",
            filterComponent: enumGenericFilter,
            uniqueFilterId: "outletCategoryFilter",
            filterKey: "ln_category__in",
            enumItems: DjangIO.app.media_monitoring.types.MediaSourceCategory.region_supported_items(),
            sortBy: enumLabelSort,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationOutletTypeFilter extends React.Component {
    static get defaultProps() {
        const { SectorType, MetaOrganizationType, NewMajorOrganizationType } = DjangIO.app.person.newtypes

        return {
            displayLabel: "Outlet Type",
            filterComponent: enumGenericFilter,
            uniqueFilterId: "outletTypeFilter",
            filterKey: "outlet_subtype",
            enumItems: NewMajorOrganizationType.items(),
            enumGroups: MetaOrganizationType.items()
                .filter((item) => item.parent === SectorType.media.value)
                .sort(enumPrecedenceSort)
                .map((item) => ({
                    title: item.label,
                    values: NewMajorOrganizationType.items()
                        .filter((subItem) => subItem.parent === item.value)
                        .map((subItem) => subItem.value),
                    collapseGroup: true,
                    sortBy: enumPrecedenceSort,
                })),
            sortBy: enumValueSort,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationFrequencyFilter extends React.Component {
    static get defaultProps() {
        return {
            filterComponent: enumGenericFilter,
            uniqueFilterId: "pubOrgFrequencyFilter",
            filterKey: "publication_frequency",
            displayLabel: "Publication Frequency",
            enumItems: DjangIO.app.media_monitoring.types.IntradoFrequency.items(),
            sortBy: enumValueSort,
            selectComponent: FrequencySelect,
            permissions: [DjangIO.app.models.PermissionType.media_relations],
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationCirculationFilter extends React.Component {
    static get defaultProps() {
        return {
            displayLabel: "Circulation",
            enumItems: DjangIO.app.media_monitoring.types.IntradoCirculation.items(),
            filterComponent: enumGenericFilter,
            filterKey: "outlet_circulation",
            helpText:
                "Results will show Media Outlets where circulation is " +
                "defined. Some Media Outlets that meet this criteria may be " +
                "excluded from the results.",
            selectComponent: CirculationSelect,
            sortBy: enumValueSort,
            uniqueFilterId: "pubOrgCirculationFilter",
            permissions: [DjangIO.app.models.PermissionType.media_relations],
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationLanguageFilter extends React.Component {
    static get defaultProps() {
        return {
            filterComponent: enumGenericFilter,
            uniqueFilterId: "pubOrgLanguageFilter",
            filterKey: "languages",
            displayLabel: "Language",
            enumItems: DjangIO.app.document.types.Language.items(),
            sortBy: enumOrderDescendingSort,
            selectComponent: LanguageSelect,
            permissions: [DjangIO.app.models.PermissionType.media_relations],
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationPressFilter extends React.Component {
    static get defaultProps() {
        return {
            filterComponent: searchifySelectGenericFilter,
            uniqueFilterId: "publicOrgPressFilter",
            filterKey: "for_press",
            searchifyFLV: PressContactSearchifyFLV,
            dataTypeLabel: DjangIO.app.models.QuorumDataType.press_contact.plural,
            displayLabel: `Associated with ${DjangIO.app.models.QuorumDataType.press_contact.plural}...`,
            selectComponent: PressContactSelect,
            permissions: [DjangIO.app.models.PermissionType.media_relations],
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationDMAFilter extends React.Component {
    static get defaultProps() {
        return {
            filterComponent: searchifySelectGenericFilter,
            uniqueFilterId: "publicOrgDMAFilter",
            filterKey: "market_area",
            dataTypeLabel: "DMA",
            displayLabel: "DMA",
            selectComponent: DMASelect,
            permissions: [DjangIO.app.models.PermissionType.media_relations],
            regions: usAndCanadaRegions,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationSchoolDistrictFilter extends React.Component {
    static get defaultProps() {
        return {
            filterComponent: searchifySelectGenericFilter,
            uniqueFilterId: "publicOrgSchoolDistrictFilter",
            filterKey: "searchify_school_district",
            dataTypeLabel: "School District",
            selectComponent: SchoolDistrictSelect,
            searchifyFLV: PublicOrganizationFilterableListView,
            regions: localRegions,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationArticleTitleFilter extends React.Component {
    static defaultProps = {
        filterComponent: textGenericFilter,
        uniqueFilterId: "publicOrganizationArticleTitleFilter",
        filterKey: "article_title_search",
        placeholderText: "Search text of headline...",
        displayLabel: "Article Headline",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationArticleFilter extends React.Component {
    static get defaultProps() {
        return {
            filterComponent: searchifySelectGenericFilter,
            uniqueFilterId: "publicOrganizationArticleFilter",
            filterKey: "associated_with_article",
            dataTypeLabel: "News Articles",
            displayLabel: "News Articles",
            searchifyFLV: ArticleFilterableListView,
            selectComponent: ArticleSelect,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class PublicOrganizationOutletCoverageFilter extends React.Component {
    static get defaultProps() {
        return {
            displayLabel: "Outlet Coverage",
            filterComponent: enumGenericFilter,
            uniqueFilterId: "outletCoverageFilter",
            filterKey: "outlet_coverage",
            enumItems: DjangIO.app.media_monitoring.types.MediaSourceCategory.region_supported_items().filter(
                (item) => item.value !== DjangIO.app.media_monitoring.types.MediaSourceCategory.general.value,
            ),
            sortBy: enumPrecedenceSort,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export const PublicOrganizationSocialMediaFilter = (props) => {
    const FilterComponent = props.filterComponent
    return <FilterComponent {...props} />
}
PublicOrganizationSocialMediaFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "publicOrganizationSocialMediaFilter",
    filterKey: "searchify_source_document",
    dataTypeLabel: "Tweets",
    searchifyFLV: SocialDocumentFilterableListView,
    selectComponent: SocialMediaSelect,
    permissions: [DjangIO.app.models.PermissionType.media_relations],
}

export const PublicOrganizationDocumentFilter = (props) => {
    const FilterComponent = props.filterComponent
    return <FilterComponent {...props} />
}
PublicOrganizationDocumentFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "publicOrganizationDocumentFilter",
    filterKey: "searchify_source_document",
    dataTypeLabel: DjangIO.app.models.QuorumDataType.document.plural,
    searchifyFLV: DocumentInOrganizationProfileFilterableListView,
    selectComponent: DocumentSelect,
    permissions: [DjangIO.app.models.PermissionType.media_relations],
}

export const OrganizationSchoolDistrictEnrollmentMinFilter = createSchoolDistrictEnrollmentFilter({
    uniqueFilterId: "org_sd_enrollment_min",
    filterKey: "school_districts_min_enrollment_size",
    isMin: true,
})

export const OrganizationSchoolDistrictEnrollmentMaxFilter = createSchoolDistrictEnrollmentFilter({
    uniqueFilterId: "org_sd_enrollment_max",
    filterKey: "school_districts_max_enrollment_size",
    isMin: false,
})
